import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Save, Layers } from 'lucide-react';
import { Button } from '../components/ui/button';
import { AutoExpandTextarea } from '../components/AutoExpandTextarea';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';
import { createFormSave } from '../lib/formAutosave';
import { useFormAutosave } from '../hooks/useFormAutosave';
import SaveStatusLabel from '../components/SaveStatusLabel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PlotSetup() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [project, setProject] = useState(null);
  const [formData, setFormData] = useState({
    logline: '',
    synopsis: '',
  });
  const [loading, setLoading] = useState(true);
  const saveClientRef = useRef(null);

  useEffect(() => {
    fetchProject();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await authFetch(`${API}/projects/${projectId}`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`load failed (${response.status})`);
      const data = await response.json();
      setProject(data);
      setFormData({
        logline: data.logline || '',
        synopsis: data.synopsis || '',
      });
      // Re-key the save client when the project changes (param-only route
      // changes don't remount). Never regress a live client's rev from a
      // fetch — seed only when it has none.
      if (saveClientRef.current?.projectId !== projectId) {
        saveClientRef.current = {
          projectId,
          client: createFormSave({
            url: `${API}/projects/${projectId}/setup`,
            initialRev: data.content_rev ?? null,
          }),
        };
      } else if (saveClientRef.current.client.getRev() == null) {
        saveClientRef.current.client.setRev(data.content_rev ?? null);
      }
    } catch (error) {
      console.error('Error fetching project:', error);
      setProject(null);
      setFormData({ logline: '', synopsis: '' });
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const persistSetup = useCallback(
    (data, meta) => saveClientRef.current.client.save(data, meta),
    [],
  );
  const { status: saveStatus, markDirty, flush, isDirty } = useFormAutosave({
    data: formData,
    save: persistSetup,
    enabled: !loading && !!project,
  });
  const saving = saveStatus?.state === 'saving';

  const handleInputChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    markDirty();
  };

  // Explicit flush — autosave already persists on a debounce; this is the
  // "save right now" button, not the only path to persistence.
  const handleSave = () => flush();

  const handleContinue = async () => {
    const allFilled = Object.values(formData).every((val) => val.trim());
    if (!allFilled) {
      toast.error('Please fill in all fields before continuing');
      return;
    }
    await flush();
    // Leaving unmounts this page — if the flush couldn't land (409, dead
    // session, offline), navigating would silently drop the setup text.
    if (isDirty()) {
      toast.error(t('autosave_navBlocked'));
      return;
    }
    navigate(`/plot/${projectId}/level/1`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-foreground font-mono">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="noise-overlay" />

      <header className="glassmorphism sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate(`/project/${projectId}?tab=plot`)}
            data-testid="back-to-workspace-btn"
            className="rounded-none font-mono uppercase tracking-widest text-xs"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Workspace
          </Button>
          <div className="text-sm font-mono" data-testid="project-title">
            {project?.title}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-12 text-center">
          <div className="flex items-center justify-center gap-4 mb-6">
            <Layers className="w-16 h-16 text-primary" />
          </div>
          <h1 className="font-mono text-3xl md:text-4xl font-semibold tracking-tight mb-2" data-testid="setup-title">
            Plot Setup
          </h1>
          <p className="text-primary text-lg font-mono">{project?.format} Format</p>
          <p className="text-muted-foreground mt-2">
            Define your story foundation with a logline and synopsis
          </p>
        </div>

        <div className="space-y-8 mb-12">
          {/* Logline */}
          <div className="space-y-3">
            <label htmlFor="logline" className="block font-mono text-sm uppercase tracking-wider text-foreground">
              Logline
            </label>
            <AutoExpandTextarea
              id="logline"
              placeholder="A one-sentence summary of your story"
              value={formData.logline || ''}
              onChange={(e) => handleInputChange('logline', e.target.value)}
              data-testid="input-logline"
              className="bg-background border-input rounded-none font-mono focus:ring-2 focus:ring-primary focus:border-transparent placeholder:text-muted-foreground/50 min-h-[80px]"
            />
          </div>

          {/* Synopsis */}
          <div className="space-y-3">
            <label htmlFor="synopsis" className="block font-mono text-sm uppercase tracking-wider text-foreground">
              Synopsis
            </label>
            <AutoExpandTextarea
              id="synopsis"
              placeholder="A brief overview of your story (2-3 paragraphs)"
              value={formData.synopsis || ''}
              onChange={(e) => handleInputChange('synopsis', e.target.value)}
              data-testid="input-synopsis"
              className="bg-background border-input rounded-none font-mono focus:ring-2 focus:ring-primary focus:border-transparent placeholder:text-muted-foreground/50 min-h-[150px]"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-btn"
              variant="outline"
              className="rounded-none font-mono uppercase tracking-widest text-xs px-6 py-3"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
            <SaveStatusLabel status={saveStatus} />
          </div>
          <Button
            onClick={handleContinue}
            disabled={saving}
            data-testid="continue-btn"
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-widest text-xs px-6 py-3 shadow-glow transition-all hover:shadow-glow-strong"
          >
            Continue to Act I
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </main>
    </div>
  );
}
